/**
 * Wire types for the Sill Pro CRM read surface.
 *
 * These mirror the Go DTOs EXACTLY (snake_case JSON), so the same TypeScript
 * shape decodes a live `/api/professional/*` response and a fixture:
 *
 *   - `ProfileResponse`        ← consumer/dto.go  (ProfileResponse, scope-projected)
 *   - `ProConnection`          ← api/professional_clients.go  (proConnectionDTO)
 *   - `ProClientRow`           ← api/professional_clients.go  (proClientRowDTO)
 *   - `ProClientListResponse`  ← api/professional_clients.go  (proClientListResponse)
 *   - `ProClientDetail`        ← api/professional_clients.go  (proClientDetailResponse)
 *   - `ProActivityEvent`       ← api/professional_events.go   (proActivityEventDTO)
 *
 * THE INVARIANT THE TYPES ENCODE: field presence IS the visibility contract.
 * The server strips fields by read scope before serializing, so a `status_only`
 * link's `profile` carries NO financial magnitudes (household_income, savings,
 * target_price, …). Every financial field is therefore optional — the UI must
 * branch on presence, never on role. A missing field means "not shared at this
 * tier", which is the on-screen analog of the backend leak guard.
 */

import type { RevertReasonCode, Stage } from "./stage";

// ─────────────────────────────────────────────────────────────────────────
// Referrals (GET /api/referrals/me, POST /api/referrals/links)
// ─────────────────────────────────────────────────────────────────────────

/** Opaque, owner-scoped link returned by the referral rail. */
export interface ReferralLink {
  token: string;
  kind: string;
  expires_at?: string | null;
  revoked_at?: string | null;
  created_at: string;
}

/** A CRM or consumer reward row. The API deliberately exposes no referee PII. */
export interface ReferralReward {
  id: string;
  kind: string;
  status: "earned" | "fulfilled" | "reversed";
  fulfilled_at?: string | null;
  reversed_at?: string | null;
  created_at: string;
}

/** Owner-scoped referral rail read model. */
export interface MyReferralsResponse {
  links: ReferralLink[];
  rewards: ReferralReward[];
  waitlist_position: number | null;
}

// ─────────────────────────────────────────────────────────────────────────
// Enums mirrored from internal/model/models.go
// ─────────────────────────────────────────────────────────────────────────

/** model.SharingLevel — the consumer's per-link disclosure tier. */
export type SharingLevel = "full_financial" | "status_only" | "none";

/** model.ConnectionMethod — how the link was established. */
export type ConnectionMethod =
  | "unknown"
  | "referral_link"
  | "manual_code"
  | "marketplace"
  | "concierge";

/**
 * Distribution-funnel state (proConnectionDTO.funnel_state). The roster only
 * enumerates active links today, so a live row is "linked"; the richer states
 * exist so the contact funnel can populate them without a wire change.
 */
export type FunnelState =
  | "added"
  | "invited"
  | "opened"
  | "signed_up"
  | "linked"
  | "unlinked";

/** The pro's relationship role on a given link. */
export type ProRole = "agent" | "lo";

export type FinancialVerificationTier =
  | "self_reported"
  | "connected"
  | "document"
  | "pre_approval";

// ─────────────────────────────────────────────────────────────────────────
// activity event_type discriminators (services/activity/projector.go)
// ─────────────────────────────────────────────────────────────────────────

export type ActivityEventType =
  | "verification_tier_changed"
  | "profile_updated"
  | "listing_analysis_run"
  | "link_changed"
  | "invite_accepted"
  | "rate_watch_shift"
  | "going_cold"
  | "lease_window_hit"
  | "band_up"
  | "document_added"
  | "bank_connected"
  | "messaged"
  | "messaged_pro"
  | "thread_message";

export type ClientActivitySource = "activity" | "sent_message";

export type TodayGroupKey =
  | "new"
  | "readiness_moved"
  | "high_intent"
  | "timing"
  | "your_followups";

// ─────────────────────────────────────────────────────────────────────────
// consumer.ProfileResponse — the scope-projected consumer profile.
// ─────────────────────────────────────────────────────────────────────────

/**
 * consumer.ProfileResponse. Every field is `omitempty` on the wire and is set
 * by exactly one of the four projections (Anonymized / StatusOnly /
 * FullFinancial / Full). The grouping comments below match dto.go so it's
 * obvious which fields a given scope can carry.
 *
 * `monthly_debts` and `publish_request_payload` are `any` on the Go side
 * (decoded JSONB) — typed loosely here for the same reason.
 */
export interface ProfileResponse {
  // ─ Identifiers (Full only) ──────────────────────────────────────────────
  id?: string;
  current_snapshot_id?: string | null;

  // ─ Anonymized-safe (marketplace browse) ─────────────────────────────────
  buyer_type?: string;
  timeline?: string;
  target_area?: string;
  /** Bucketed band, e.g. "600k-700k" — never the exact price at this tier. */
  target_price_band?: string;
  price_kind?: "shopping_range" | "property_bracket" | "target_bracket";
  price_approximate?: boolean;
  profile_completeness?: number;

  // ─ Status-only (connected pro, NO financial magnitudes) ──────────────────
  display_name?: string;
  housing_situation?: string;
  lease_expiration?: string;
  published_at?: string;

  // ─ Full financial (connected pro, full sharing) ──────────────────────────
  household_income?: number;
  savings?: number;
  credit_score_range?: string;
  monthly_debts?: unknown;
  target_price?: number;
  budget_monthly?: number;
  reserve_floor_months?: number;
  comfort_dti_pct?: number;
  employment_months?: number;
  recent_job_gap?: boolean;
  has_second_job?: boolean;
  is_self_employed?: boolean;
  has_bonus_income?: boolean;
  has_commission_income?: boolean;

  // ─ Affordability headline (SILL-383 read-model / SILL-384 M3) ─────────────
  // The readiness/qualification OUTCOME overlaid by ProjectProfileForRole at
  // ReadFullFinancial — band + qual price + movement + down-payment-ready. Nil
  // fields → pending (the UI never fabricates a number). `affordability_movement`
  // and `down_payment_ready` are partner-lens only (absent from the consumer
  // self-view projection — the role boundary scenario 46 locks).
  affordability_band_low?: number;
  affordability_band_high?: number;
  sill_qual_price?: number;
  affordability_movement?: AffordabilityMovementResponse;
  down_payment_ready?: boolean;
  // Readiness OUTCOME (2026-06-10 redesign): Sill score (absent for a DNQ
  // client), segment enum, and human label — DISPLAYED off the frozen snapshot,
  // never recomputed. Surfaced to agent + LO at ReadFullFinancial only.
  readiness_score?: number | null;
  readiness_segment?: string;
  readiness_label?: string;

  // ─ Full (owner / internal) — never present on a pro read ──────────────────
  portal_discoverable?: boolean;
  portal_show_readiness?: boolean;
  portal_show_preferences?: boolean;
  portal_show_timeline?: boolean;
  publish_requested_at?: string;
  publish_request_payload?: unknown;
  created_at?: string;
  updated_at?: string;
}

/**
 * consumer.AffordabilityMovementResponse — the recent band-high delta between
 * two profile snapshots (partner-lens only). Every field is `omitempty`: nil
 * when no prior snapshot exists or the band didn't move → no movement chip.
 */
export interface AffordabilityMovementResponse {
  band_high_from?: number;
  band_high_to?: number;
  /** RFC3339 timestamp of the snapshot the movement was measured to. */
  as_of?: string;
}

// ─────────────────────────────────────────────────────────────────────────
// Connection metadata (proConnectionDTO) — safe at any non-denied scope.
// ─────────────────────────────────────────────────────────────────────────

export interface ProConnection {
  link_id: string;
  /**
   * The linked consumer profile id — the canonical nav target for the client
   * detail route (`/clients/:profileId`). Present at any non-denied scope (an
   * opaque UUID; access is still gated server-side by policy.Check), regardless
   * of whether the financial `profile` projection is included. Navigate by THIS,
   * never `link_id` (which the detail endpoint can't resolve).
   */
  profile_id: string;
  role: ProRole;
  sharing_level: SharingLevel;
  /** connection_method on the wire; omitted when unknown. */
  connection_method?: ConnectionMethod;
  funnel_state: FunnelState;
  /** linked_at, RFC3339; omitted when zero. */
  since?: string;
}

// ─────────────────────────────────────────────────────────────────────────
// Activity event (proActivityEventDTO).
// ─────────────────────────────────────────────────────────────────────────

/**
 * proActivityEventDTO. `payload` is the ALREADY-projected (scope-redacted)
 * event payload: at status_only it carries only the allowlisted, magnitude-free
 * keys for its event_type (services/activity/projector.go), so a status_only
 * row has labels/directions but no figures.
 */
export interface ProActivityEvent {
  event_type: ActivityEventType;
  profile_id: string;
  display_name?: string;
  /** RFC3339. */
  occurred_at: string;
  /** Scope-redacted JSON object — never trust it to carry magnitudes. */
  payload: ActivityPayload;
  connection: ProConnection;
}

/** Pro-owned outbound sent-log row. */
export interface ProSentMessage {
  /** Present only when the provider accepted the message. */
  subject?: string;
  body?: string;
  channel: string;
  /** Safe owner-visible delivery outcome. Legacy rows default to sent. */
  outcome?: "sent" | "skipped" | "failed" | "attention";
  /** Bounded non-content reason for a non-sent outcome. */
  reason?: string;
  sent_at: string;
}

export interface ProClientSentMessagesResponse {
  sent_messages: ProSentMessage[];
}

// ─────────────────────────────────────────────────────────────────────────
// Today queue (GET /api/professional/today) + manual tasks.
// ─────────────────────────────────────────────────────────────────────────

export interface CRMTask {
  id: string;
  professional_id: string;
  contact_id?: string;
  contact?: CRMContact | null;
  title: string;
  due_at?: string;
  done_at?: string;
  created_at: string;
}

export interface TodayQueueItem {
  item_key: string;
  group: TodayGroupKey;
  profile_id?: string;
  event_id?: string;
  event_type?: ActivityEventType;
  occurred_at?: string;
  profile?: ProfileResponse | null;
  sharing_level?: SharingLevel;
  /**
   * ENRICHMENT data (plan 378 §3.2): absent when this person's enrichment is
   * locked (Free-tier plan) or unavailable (sharing below full financials).
   * Absent means UNKNOWN — never default it to "self_reported".
   */
  verification_status?: FinancialVerificationTier;
  /**
   * D3 discriminator for the item's enrichment-grade fields (tier, the
   * profile affordability/readiness overlay, tier keys in payload). Branch on
   * THIS, never on field absence. Absent on task items and pre-rollout
   * backends.
   */
  enrichment_state?: EnrichmentState;
  payload?: ActivityPayload;
  last_sent_at?: string;
  last_activity_at?: string;
  task?: CRMTask | null;
}

export interface TodayGroup {
  key: TodayGroupKey;
  count: number;
  items: TodayQueueItem[];
}

/** Wire shape for one pending automation draft in the Today queue. */
export interface TodayDraftResponse {
  /** automation_pending_drafts.id */
  id: string;
  /** Parent automation's UUID. */
  automation_id: string;
  /** Buyer whose draft is awaiting review. */
  profile_id: string;
  /** Message template to review before sending. */
  template_id: string;
  /** When the draft was enqueued. */
  created_at: string;
}

/**
 * Wire shape for the pending marketplace-connection-requests summary
 * surfaced on Today (plan 239, M6-B). Just enough to render the row's
 * badge + CTA copy — never the buyer's identity (that lives behind the
 * anonymized `/api/marketplace/pro/requests` list, not here).
 */
export interface TodayMarketplaceRequestsSummary {
  count: number;
  /** Frozen fee-quote net_cents of the request the row leads with. */
  top_fee_cents?: number;
}

export interface TodayQueueResponse {
  groups: TodayGroup[];
  snoozed?: TodayQueueItem[];
  /**
   * Pending automation drafts. NOT included in open_count — surfaced
   * separately and do not contribute to the Today badge count.
   */
  drafts?: TodayDraftResponse[];
  /**
   * Pending marketplace connection requests. NOT included in open_count —
   * same badge discipline as `drafts` above. Today's own endpoint does not
   * populate this today (frontend derives it via a separate
   * `listMarketplaceProRequests` call); the field exists so the shape is
   * ready if/when the backend starts sending it.
   */
  pending_marketplace_requests?: TodayMarketplaceRequestsSummary;
  open_count: number;
  linked_count: number;
  truncated?: boolean;
}

/**
 * Loose shape over the union of allowlisted payload keys across event types
 * (projector.go `statusOnlyFields`) plus the full_financial-only magnitudes.
 * All optional — presence depends on event_type AND the link's resolved scope.
 */
export interface ActivityPayload {
  // status_only-safe keys (label/direction/area/window — magnitude-free)
  tier?: string;
  tier_label?: string;
  changed_fields?: string[];
  direction?: string;
  area?: string;
  price_band?: string;
  action?: string;
  role?: string;
  since?: string;
  name?: string;
  message?: string;
  headline?: string;
  qualitative?: string;
  inactive_days?: number;
  window?: string;
  band?: string;
  doc_type?: string; // document_added — category label (status_only-safe)
  channel?: string; // messaged — outreach channel ("1on1" | "broadcast"), status_only-safe
  // full_financial-only magnitudes (separate top-level keys, never nested)
  address?: string;
  list_price?: number;
  affordability?: string;
  new_budget?: number;
  [key: string]: unknown;
}

// ─────────────────────────────────────────────────────────────────────────
// Roster + detail envelopes.
// ─────────────────────────────────────────────────────────────────────────

/** proClientRowDTO — one row in GET /api/professional/clients. */
export interface ProClientRow {
  /** Lifted to the top level so layout can branch without re-deriving it. */
  sharing_level: SharingLevel;
  connection: ProConnection;
  /** Scope-projected; null only defensively (server sends an object). */
  profile: ProfileResponse | null;
  /** Backend-owned consumer verification status for the CRM badge. */
  verification_status?: FinancialVerificationTier;
  /**
   * Scope-aware count of recent activity events for this client (SILL-348).
   * Drives the roster "Activity" pill. Absent on the detail envelope (which
   * carries the full activity_timeline instead).
   */
  recent_event_count?: number;
  /**
   * Today overlay from GET /api/professional/today and the roster list.
   * The live wire response always includes it; the field stays optional so
   * older hand-authored fixtures keep compiling.
   */
  needs_response?: boolean;
  /**
   * RFC3339 timestamp of the most recent scope-visible activity event for this
   * client (SILL-372 AC4). Drives the roster "Last active" line so it reflects
   * real activity rather than the connection date. Absent when the client has no
   * visible recent activity (the adapter then falls back to the connection date).
   */
  last_activity_at?: string;
  /**
   * The pro-owned crm_contacts row id for this linked client (M3). Absent when
   * no contact record is linked to the profile (use PATCH /contacts/:id to edit
   * tags). Present → enables tag read-through + write-back on the client detail.
   */
  contact_id?: string;
  /**
   * Tags from the linked crm_contacts row (M3). Always `[]` when no contact
   * record is found (backend normalizes nil → []). Optional in the type so
   * pre-M3 test fixtures compile without the field.
   */
  contact_tags?: string[];
  /**
   * Contact email + phone read-through from the linked crm_contacts row
   * (SILL-427). Absent when no contact is linked or the field is blank.
   */
  contact_email?: string;
  contact_phone?: string;
  /** RFC3339 timestamp of the most recent pro-owned sent message. */
  last_sent_at?: string;
  /**
   * M8-B relationship-pipeline stage (plan 291, D8 rev 4), read-through from
   * the linked crm_contacts row. Absent when no contact is linked yet — never
   * a fabricated default. See lib/stage.ts for the machine.
   */
  stage?: Stage;
  /** RFC3339 timestamp of the most recent stage write. Absent with `stage`. */
  stage_updated_at?: string;
  /**
   * D12 derived "quiet for 30+ days" overlay (plan 291 M8-B, task 1400).
   * Computed server-side at read time from stage + stage_updated_at, NEVER
   * stored. Absent/false means not dormant.
   */
  dormant?: boolean;
}

/** proClientListResponse. */
export interface ProClientListResponse {
  clients: ProClientRow[];
  next_cursor?: string;
}

/** proClientDetailResponse — a row plus the read:activity timeline. */
export interface ProClientDetail {
  sharing_level: SharingLevel;
  connection: ProConnection;
  profile: ProfileResponse | null;
  verification_status?: FinancialVerificationTier;
  /** Present-but-empty when the consumer has share_with_pro off. */
  activity_timeline: ProActivityEvent[];
  /** Pro-owned sent log for the detail timeline. */
  sent_messages?: ProSentMessage[];
  /** Most recent scope-visible activity timestamp (SILL-372 AC4); see ProClientRow. */
  last_activity_at?: string;
  /** Pro-owned crm_contacts row id for this client (M3). See ProClientRow. */
  contact_id?: string;
  /** Tags from the linked crm_contacts row (M3). See ProClientRow. */
  contact_tags?: string[];
  /**
   * crm_contacts.notes read-through from the linked contact row (M4 notes
   * bridge). The pinned "Contact note" card on client detail reads this and
   * writes back via PATCH /contacts/:id (D4 chokepoint). Empty / absent when
   * no contact row is linked or the contact has no note.
   */
  contact_note?: string;
  /**
   * Contact email + phone read-through from the linked crm_contacts row
   * (SILL-427). The "Contact info" block on client detail reads these. Absent
   * when no contact is linked or the field is blank.
   */
  contact_email?: string;
  contact_phone?: string;
  /** Most recent pro-owned sent message timestamp. */
  last_sent_at?: string;
  /** M8-B relationship-pipeline stage. See ProClientRow.stage. */
  stage?: Stage;
  /** RFC3339 timestamp of the most recent stage write. Absent with `stage`. */
  stage_updated_at?: string;
  /** D12 derived "quiet for 30+ days" overlay. See ProClientRow.dormant. */
  dormant?: boolean;
  /**
   * Plan 291 round-3 "Record agreement" affordance: the pro's OWN active
   * marketplace connection id for this client (already on the pro's own
   * Connections ledger, never buyer PII). Absent for an invited-only or
   * non-marketplace client. Detail-only — not on `ProClientRow`.
   */
  connection_id?: string;
}

/** model.CRMClientNote — private pro-owned notes for one connected client. */
export interface ProClientNote {
  id: string;
  professional_id: string;
  profile_id: string;
  body: string;
  created_at: string;
  updated_at: string;
}

/**
 * GET /api/professional/people/:contactId/notes envelope — renamed from the
 * retired /clients/:profileId/notes path (plan 377 M5, design spec §4.1).
 */
export interface ProClientNotesResponse {
  notes: ProClientNote[];
}

// ─────────────────────────────────────────────────────────────────────────
// People roster (GET /api/professional/people).
// ─────────────────────────────────────────────────────────────────────────

/**
 * Relationship stage and Sill status are deliberately separate state
 * machines. Mirrors backend crm.SillStatus (person-model-completion design
 * §3.1) — it is a fact about the relationship, never a permission; gate
 * controls on Capabilities, not this.
 */
export type SillStatus = "not_invited" | "invited" | "joined_sill" | "connected";

/**
 * Mirrors backend crm.EnrichmentState (design §3.2). The four values are NOT
 * reducible to a bool: `locked` and `unavailable` are both connected people
 * whose data this pro cannot read, for opposite reasons — one is an upgrade
 * prompt, the other is a retry.
 */
export type EnrichmentState = "not_connected" | "available" | "locked" | "unavailable";

/**
 * Mirrors backend crm.EnrichmentData — ONLY what connection unlocks. Contact
 * identity, tags, notes, and stage live on ProPersonRow itself and must
 * never be duplicated here.
 */
export interface EnrichmentData {
  profile_id: string;
  verification_status?: FinancialVerificationTier;
  affordability_low?: number;
  affordability_high?: number;
  movement?: AffordabilityMovementResponse;
  readiness_score?: number;
  readiness_segment?: string;
  readiness_label?: string;
}

/**
 * Mirrors backend crm.Enrichment (design §3.2) as a REAL discriminated
 * union: `data` is unreachable outside the "available" arm, so a
 * non-exhaustive switch over `state` is a compile error the moment a fifth
 * state is added. State is the discriminator — never branch on `data`.
 */
export type Enrichment =
  | { state: "not_connected" }
  | { state: "locked" }
  | { state: "unavailable" }
  | { state: "available"; data: EnrichmentData };

/** Mirrors backend crm.CapabilityReason (design §3.4). */
export type CapabilityReason =
  | "archived"
  | "not_connected"
  | "already_connected"
  | "no_email"
  | "no_phone"
  | "tier_locked";

// ─────────────────────────────────────────────────────────────────────────
// Runtime enum guards (design §8.2 "Enum parity"). TS unions have no runtime
// representation, so these mirror the Go ParseSillStatus / ParseEnrichmentState
// / ParseCapabilityReason fail-closed contract: an unrecognized value is
// rejected, never silently accepted. Both sides are pinned against the SAME
// backend/internal/services/crm/testdata/person_enums.json fixture.
// ─────────────────────────────────────────────────────────────────────────

const SILL_STATUS_VALUES: readonly SillStatus[] = [
  "not_invited",
  "invited",
  "joined_sill",
  "connected",
];
export function isSillStatus(value: string): value is SillStatus {
  return (SILL_STATUS_VALUES as readonly string[]).includes(value);
}

const ENRICHMENT_STATE_VALUES: readonly EnrichmentState[] = [
  "not_connected",
  "available",
  "locked",
  "unavailable",
];
export function isEnrichmentState(value: string): value is EnrichmentState {
  return (ENRICHMENT_STATE_VALUES as readonly string[]).includes(value);
}

// CapabilityReason additionally admits "" (the "allowed, no reason" zero
// value) — it is never itself the empty string's assertion target on the
// wire, but a Capability with allowed=true legitimately carries no reason.
const CAPABILITY_REASON_VALUES: readonly CapabilityReason[] = [
  "archived",
  "not_connected",
  "already_connected",
  "no_email",
  "no_phone",
  "tier_locked",
];
export function isCapabilityReason(value: string): value is CapabilityReason | "" {
  return value === "" || (CAPABILITY_REASON_VALUES as readonly string[]).includes(value);
}

/**
 * Mirrors backend crm.Capability: an allowed/denied decision plus the
 * reason it was denied. `reason` is set iff `!allowed` for every capability
 * the resolver currently gates (CanRestore is the one documented exception —
 * see backend capability.go).
 */
export interface Capability {
  allowed: boolean;
  reason?: CapabilityReason;
}

export interface ProPersonEngagement {
  attention: number;
  /** One server-authorized, body-free cue for the retained Today signal. */
  actionable_reason?: string;
  next_task_due_at?: string;
  latest_note_at?: string;
  latest_invite_at?: string;
  latest_activity_at?: string;
  last_outbound_at?: string;
  last_outbound_status?: string;
}
/**
 * Mirrors backend crm.Capabilities. `can_marketplace_action` is
 * DELIBERATELY absent (D8) — it was removed rather than reconciled; a
 * future marketplace affordance returns as several named capabilities, not
 * one boolean.
 */
export interface ProPersonCapabilities {
  can_invite: Capability; can_email: Capability; can_schedule: Capability; can_task: Capability;
  can_note: Capability; can_in_app_message: Capability;
  can_archive: Capability; can_restore: Capability; can_delete: Capability;
}
/**
 * `contact_id`, never optional enrichment, is the durable People row
 * identity. `enrichment` mirrors backend crm.Person.Enrichment exactly —
 * one nested {state, data?} object, never a sibling
 * affordability/readiness_summary/enrichment_state trio that could
 * disagree with it.
 */
export interface ProPersonRow {
  contact_id: string;
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  source: ContactSource;
  tags: string[];
  area?: string;
  timeline?: string;
  archived_at?: string;
  /** When the pro added the person; the recency fallback for someone never touched. */
  created_at?: string;
  relationship_stage: Stage;
  stage_updated_at?: string;
  sill_status: SillStatus;
  engagement: ProPersonEngagement;
  capabilities: ProPersonCapabilities;
  enrichment: Enrichment;
}
export interface ProPeopleCounts {
  all: number; active: number; needs_me: number; ready_soon: number;
  going_cold: number; archived: number;
}
export interface ProPeopleListResponse {
  people: ProPersonRow[];
  next_cursor?: string;
  counts: ProPeopleCounts;
}

/**
 * Detail remains contact-keyed; optional enrichment is already
 * policy-projected. The SAME `enrichment` shape as the roster row lives on
 * `person` — no second, disagreeing enrichment representation at this
 * response's top level.
 */
export interface ProPersonDetailResponse {
  person: ProPersonRow;
  notes: Array<{ id: string; body: string; created_at: string; updated_at: string }>;
  /**
   * The pro-owned bio note (crm_contacts.notes) — distinct from the
   * timestamped `notes` LIST above. Plan 377 M5 addition, detail-only
   * (never on ProPersonRow / the roster row — see the backend doc comment
   * on proPersonDetailResponse for the privacy rationale).
   */
  contact_note: string;
}

/** Explicitly body-free chronology contract for an expanded Person row. */
export interface ProPersonActivityResponse {
  activity: Array<{
    event_key: string;
    kind: string;
    summary: string;
    occurred_at: string;
    status?: string;
    source: string;
  }>;
  next_cursor?: string;
  unavailable_sources: string[];
}
export interface ProPeopleQuery {
  query?: string; quick_view?: string; relationship_stage?: string[];
  sill_status?: string[]; verification_tier?: string[]; timeline?: string[];
  tag?: string[]; role?: ProRole; cursor?: string;
}

// ─────────────────────────────────────────────────────────────────────────
// Display-layer query params (handled server-side; mirrored for the hook).
// ─────────────────────────────────────────────────────────────────────────

/** "attention" (default) | "recent" — the only two server sorts. */
export type ProClientSort = "attention" | "recent";

/** Query params accepted by GET /api/professional/clients. */
export interface ProClientsQuery {
  role?: ProRole;
  /** substring over projected target_area */
  q?: string;
  funnel_state?: FunnelState;
  sort?: ProClientSort;
  limit?: number;
  cursor?: string;
  /**
   * OR-match: clients whose linked contact has ANY of these tags.
   * Filtered service-side (in-memory over hydrated ContactTags) per ADR-033.
   * Pass a one-element array for single-tag filtering.
   */
  tags?: string[];
}

// ─────────────────────────────────────────────────────────────────────────
// Contacts (book of business) — api/professional_contacts.go.
//
// A contact is the pro's OWN annotation record (no consumer financial PII).
// `linked_profile_id` is a soft ref that is set ONLY at consumer_pro_link
// creation — never via a pre-link email scan (model.ContactFunnelState doc).
// The UI must therefore NEVER imply Sill-membership of an un-linked contact.
// ─────────────────────────────────────────────────────────────────────────

/** model.ContactSource — how the contact entered the book. */
export type ContactSource =
  | "manual"
  | "csv_import"
  | "referral"
  | "marketplace"
  | "unknown";

/** model.CRMContact — the pro-owned contact row (serialized wholesale). */
export interface CRMContact {
  id: string;
  professional_id: string;
  role: string; // "agent" | "lo"
  email?: string;
  phone?: string;
  first_name: string;
  last_name: string;
  notes: string;
  /** Normalized to [] on the wire. */
  tags: string[];
  source: ContactSource;
  import_batch_id?: string;
  /** Soft UUID ref — present ONLY for a linked (connected) contact. */
  linked_profile_id?: string;
  /** Freeform geographic area annotation (e.g. "Seattle", "Eastside"). Added in M1. */
  area: string;
  /** Freeform purchase-timeline annotation (e.g. "6 months", "ready now"). Added in M1. */
  horizon: string;
  /**
   * RFC3339 timestamp when the contact was archived; absent (null) when active.
   * Archived contacts are excluded from default list queries and funnel counts.
   */
  archived_at?: string | null;
  created_at: string;
  updated_at: string;
  /**
   * M8-B relationship-pipeline stage (plan 291, D8 rev 4). Always present on
   * the live wire (crm_contacts.stage defaults to "connected" — every
   * contact has a stage home); optional here so pre-M8-B hand-authored test
   * fixtures keep compiling. See lib/stage.ts for the machine.
   */
  stage?: Stage;
  /** RFC3339 timestamp of the most recent stage write. */
  stage_updated_at?: string;
}

/** proContactDTO — one contact row + its derived funnel position. */
export interface ProContactDTO {
  contact: CRMContact;
  funnel_state: FunnelState;
  /**
   * RFC3339 timestamp after which the contact is eligible for a new invite.
   * Null / absent when no dispatched invite exists or the 72h window cleared.
   * The FE uses this to disable the invite button with "Invited Xd ago" copy.
   * D2: anchored on dispatched_at; NULL dispatched_at → no cooldown.
   */
  invite_cooldown_until?: string | null;
  /**
   * D12 derived "quiet for 30+ days" overlay (plan 291 M8-B, task 1400).
   * Computed server-side from contact.stage + contact.stage_updated_at,
   * NEVER stored — a top-level sibling of `contact` (not a CRMContact
   * field) because it's derived, not part of the row model. Absent/false
   * means not dormant.
   */
  dormant?: boolean;
}

/**
 * The per-page funnel summary. Always emits every state zero-filled
 * (funnelSummaryWire), so the keys are stable. "unlinked" is never produced
 * by the summary writer.
 */
export type ContactFunnelSummary = Record<
  Exclude<FunnelState, "unlinked">,
  number
>;

/** proContactsListResponse — GET /api/professional/contacts. */
export interface ProContactsListResponse {
  contacts: ProContactDTO[];
  funnel_summary: ContactFunnelSummary;
  next_cursor?: string;
}

/** proEventsResponse — GET /api/professional/events. */
export interface ProEventsResponse {
  events: ProActivityEvent[];
  next_cursor?: string;
}

/** createContactRequest — POST /api/professional/contacts (manual add). */
export interface CreateContactRequest {
  email?: string;
  phone?: string;
  first_name?: string;
  last_name?: string;
  tags?: string[];
  notes?: string;
}

/**
 * updateContactRequest — PATCH /api/professional/people/:id (renamed from
 * the retired /contacts/:id path, plan 377 M5, design spec §4.1).
 * Full-state PATCH: always send ALL fields from drawer local state.
 * Last-write-wins; partial sends overwrite with empty strings.
 */
export interface UpdateContactRequest {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  area: string;
  horizon: string;
  notes: string;
  tags: string[];
}

/**
 * archiveContactRequest — POST /api/professional/people/:id/archive
 * (renamed from the retired /contacts/:id/archive path, plan 377 M5, design
 * spec §4.1).
 */
export interface ArchiveContactRequest {
  archived: boolean;
}

// ─────────────────────────────────────────────────────────────────────────
// M8-B relationship stage — POST .../clients/:profileId/stage and
// POST .../contacts/:id/stage (plan 291, D8 rev 4). Same body/response shape
// on both endpoints — the profileId->contact resolution is server-internal.
// ─────────────────────────────────────────────────────────────────────────

/** stageActionRequest — shared body for both stage-action endpoints. */
export interface StageActionRequest {
  action: "advance" | "revert";
  from_stage: Stage;
  to_stage: Stage;
  /**
   * Required for action="revert"; ignored for "advance". MUST be a
   * RevertReasonCode (lib/stage.ts) — the backend rejects any other value
   * with 400 invalid_revert_reason (ADR-030 D18 review fix: this used to be
   * free text collected via a <textarea> and it flowed straight into
   * analytics).
   */
  reason?: RevertReasonCode;
  /**
   * Optional free text for action="revert", capped at 500 chars. Flows ONLY
   * into the crm_stage_events evidence jsonb (the DB audit trail) — never
   * analytics. Ignored for "advance".
   */
  note?: string;
}

/** stageOutcomeResponse — the 200 body for a successful stage transition. */
export interface StageOutcome {
  stage: Stage;
  stage_updated_at: string;
  contact_id?: string;
}

// ─────────────────────────────────────────────────────────────────────────
// Marketplace agreement attestation — POST
// .../marketplace/pro/connections/:id/agreement (plan 291 round-3;
// backend/internal/api/marketplace_agreement.go). action="signed" is the
// D10 connected->agreement stage-transition trigger (the ONE forward edge
// out of "connected"); action="sent" is a non-transitioning status note.
// ─────────────────────────────────────────────────────────────────────────

/** postAgreementActionBody — the pro-side action request body. */
export interface AgreementActionRequest {
  action: "sent" | "signed";
}

/** agreementDTO — the wire projection of relationship.Agreement. */
export interface AgreementDTO {
  sent_at?: string;
  signed_at?: string;
  executed_via?: string;
  flagged?: boolean;
}

/** The 200 body for POST .../connections/:id/agreement. */
export interface AgreementActionResponse {
  agreement: AgreementDTO | null;
}

// ─────────────────────────────────────────────────────────────────────────
// CSV import — api/professional_imports.go + crm/imports.go.
// ─────────────────────────────────────────────────────────────────────────

/**
 * model.ImportColumnMapping — each value is a 0-based column index into the
 * CSV header row, or -1 when unmapped. Email is the only required mapping.
 */
export interface ImportColumnMapping {
  email: number;
  first_name: number;
  last_name: number;
  phone: number;
}

/** crm.ImportPreview — the StageImport (upload) result. */
export interface ImportPreview {
  batch_id: string;
  filename: string;
  headers: string[];
  suggested_mapping: ImportColumnMapping;
  /** First N data rows (raw cell strings, aligned to `headers`). */
  preview_rows: string[][];
  total_rows: number;
}

/**
 * commitImportRequest mapping body. Fields are pointers server-side so an
 * omitted field is distinguishable from an explicit column 0; we send the
 * confirmed indices and omit unmapped (-1) fields.
 */
export interface CommitMappingBody {
  email?: number;
  first_name?: number;
  last_name?: number;
  phone?: number;
}

/** crm.ImportCommitResult — partitions every data row into one bucket. */
export interface ImportCommitResult {
  imported: number;
  skipped_duplicate: number;
  invalid: number;
  imported_contact_ids?: string[];
}

// ─────────────────────────────────────────────────────────────────────────
// Mass invite — api/professional_invites.go + crm/invites.go.
//
// CRITICAL: the result carries NO per-email detail AND no separate suppressed
// count. A dispatched recipient and a silently-suppressed recipient both count
// in `sent` — a distinct suppressed count was itself a membership oracle (a pro
// could POST one contact and read suppressed=1 vs sent=1 to probe the global
// suppression list). Folding them makes a one-contact send indistinguishable.
// ─────────────────────────────────────────────────────────────────────────

/** sendInvitesRequest — POST /api/professional/invites. */
export interface SendInvitesRequest {
  contact_ids: string[];
  message: string;
}

/** crm.InviteSendResult — aggregate per-contact counts only. */
export interface InviteSendResult {
  /** Recipients accepted for sending — a real dispatch OR a silent suppression. */
  sent: number;
  skipped_already_sent: number;
  /** Contacts skipped because they were invited within the last 72 hours. */
  skipped_cooldown: number;
  invalid: number;
  /** Contacts not sent because today's invite budget was spent (SEC-06). */
  skipped_rate_limited?: number;
  /** Set only when skipped_rate_limited > 0: when the budget resets (RFC3339). */
  resets_at?: string;
  limit?: number;
  plan?: string;
}

/** crm.InviteResendResult — the resend counter only (no dispatched signal). */
export interface InviteResendResult {
  resend_count: number;
}

// ─────────────────────────────────────────────────────────────────────────
// Pro profile + founding — api/professional_profile.go + services/pro.
// ─────────────────────────────────────────────────────────────────────────

/** model.AgentProfile — owner-scoped agent profile. */
export interface AgentProfile {
  id: string;
  user_id: string;
  bio: string;
  brokerage: string;
  license_num?: string;
  /** Decoded JSONB; string[] in practice. */
  service_areas: unknown;
  invite_code?: string;
  pro_type: string;
  program_id?: string;
  /** Public founding-page opt-in. */
  directory_public: boolean;
  /** Agent booking / scheduling page URL (e.g. Calendly link). */
  booking_link?: string;
  created_at: string;
  updated_at: string;
}

/** model.LOProfile — owner-scoped loan-officer profile. */
export interface LOProfile {
  id: string;
  user_id: string;
  nmls_number?: string;
  loan_types: unknown;
  affiliation: string;
  rate_positioning?: string;
  service_areas: unknown;
  created_at: string;
  updated_at: string;
}

/** pro.FoundingProgress — deterministic, recomputed counters (no stored score). */
export interface FoundingProgress {
  invites_sent: number;
  clients_linked: number;
  clients_verified: number;
}

/** pro.FoundingStatus — the embedded founding block. */
export interface FoundingStatus {
  member: boolean;
  program_slug?: string;
  /** Backend-owned founding linked-client target. */
  target: number;
  progress: FoundingProgress;
}

/**
 * pro.ProProfile — GET/PATCH /api/professional/profile. Exactly ONE of
 * agent / lo is non-null (the pro is one role); founding is always present.
 */
export interface ProProfile {
  agent?: AgentProfile | null;
  lo?: LOProfile | null;
  founding: FoundingStatus;
}

/** updateProProfileRequest — PATCH body (only these fields are editable). */
export interface UpdateProProfileRequest {
  bio?: string;
  brokerage?: string;
  directory_public?: boolean;
  booking_link?: string;
  // Replace the stored service-areas list. Omitted = don't touch; [] clears it.
  service_areas?: string[];
}

// ─────────────────────────────────────────────────────────────────────────
// Notification preferences — api/notification_prefs.go.
// ─────────────────────────────────────────────────────────────────────────

export type NotificationChannel = "email" | "sms" | "in_app";
export type NotificationFrequency =
  | "realtime"
  | "daily"
  | "weekly"
  | "monthly"
  | "off";

/** model.NotificationPref. */
export interface NotificationPref {
  id?: string;
  user_id?: string;
  channel: NotificationChannel;
  /** Open-ended category, e.g. "weekly_digest" | "buyer_signals". */
  category: string;
  frequency: NotificationFrequency;
  created_at?: string;
  updated_at?: string;
}

/** GET/PUT /api/notification-prefs envelope. */
export interface NotificationPrefsResponse {
  email_delivery_blocked?: boolean;
  preferences: NotificationPref[];
}

// ─────────────────────────────────────────────────────────────────────────
// Batch invite — api/professional_batch_invites.go.
// ─────────────────────────────────────────────────────────────────────────

/** POST /api/professional/invites/preflight response — six eligibility buckets. */
export interface InvitePreflightResult {
  eligible: string[];
  already_invited: string[];
  signed_up: string[];
  linked: string[];
  no_email: string[];
  suppressed: string[];
  /** Present and true when the server capped the filter-mode candidate set. */
  truncated?: boolean;
}

/** POST /api/professional/invites/batch response — job_id for polling. */
export interface BatchInviteStartResult {
  job_id: string;
}

/** Batch job failed-entry on the wire. */
export interface BatchInviteFailedEntry {
  contact_id: string;
  reason: "transport" | "suppressed_at_send" | "became_ineligible";
}

/** GET /api/professional/invites/batch/:job_id response. */
export interface BatchInviteJobStatus {
  /** "queued" | "running" | "done" */
  status: string;
  total: number;
  sent: number;
  failed: BatchInviteFailedEntry[];
}

// ─────────────────────────────────────────────────────────────────────────
// Marketplace payments (M6-A) — api/marketplace_pro_billing.go,
// api/marketplace_rate_card.go.
// ─────────────────────────────────────────────────────────────────────────

/**
 * GET /api/marketplace/pro/billing response. Customer-safe billing status —
 * never a Stripe customer/payment-method id (server pins this on purpose).
 */
export interface BillingStatus {
  has_card: boolean;
  brand: string;
  last4: string;
  exp_month: number;
  exp_year: number;
}

/** POST /api/marketplace/pro/billing/setup-intent response. */
export interface SetupIntentResponse {
  client_secret: string;
}

/** One row of the pro-scoped rate card — api/marketplace_rate_card.go. */
export interface ProRateCardRow {
  cause: string;
  tier: string;
  list_cents: number;
  discount_cents: number;
  grant_label?: string;
  net_cents: number;
}

/** GET /api/marketplace/pro/rate-card response. */
export interface ProRateCardResponse {
  rates: ProRateCardRow[];
}
